import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  Line,
} from 'recharts';
import {
  useDeleteLink,
  useLink,
  useLinkStats,
  useUpdateLink,
} from '../api/hooks.js';
import LinkForm from '../components/LinkForm.js';
import Modal from '../components/Modal.js';
import QRPreview from '../components/QRPreview.js';
import { shortUrlFor } from '../lib/config.js';
import { compactNumber, formatDate } from '../lib/format.js';
import { useCanManageLink } from '../lib/perms.js';
import type { StatsBreakdown } from '../api/types.js';

type Range = '7d' | '30d' | '90d' | 'all';

export default function LinkDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('30d');
  const [editing, setEditing] = useState(false);

  const link = useLink(id);
  const stats = useLinkStats(id, range);
  const update = useUpdateLink(id);
  const del = useDeleteLink();
  const canManage = useCanManageLink();

  if (link.isLoading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (!link.data) {
    return (
      <div className="text-sm text-slate-500">
        Link not found.{' '}
        <Link to="/app/dashboard" className="text-brand-600">Back to dashboard</Link>
      </div>
    );
  }

  const l = link.data;
  const shortUrl = shortUrlFor(l.slug);

  return (
    <div>
      <Link to="/app/dashboard" className="text-sm text-slate-500 hover:text-slate-700">
        ← All links
      </Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="font-mono text-2xl text-slate-900">{l.slug}</h1>
          {l.title && <p className="mt-1 text-slate-600">{l.title}</p>}
          <p className="mt-2 text-sm break-all">
            <a
              href={shortUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-brand-700 hover:underline"
            >
              {shortUrl}
            </a>
            <span className="mx-1 text-slate-400">→</span>
            <a
              href={l.destination_url}
              target="_blank"
              rel="noreferrer"
              className="text-slate-600 hover:text-slate-900"
            >
              {l.destination_url}
            </a>
          </p>
        </div>
        {canManage(l) ? (
          <div className="flex w-full gap-2 sm:w-auto">
            <button className="btn-secondary flex-1 sm:flex-none" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              className="btn-danger flex-1 sm:flex-none"
              onClick={async () => {
                if (!confirm(`Delete adhc.to/${l.slug}?`)) return;
                await del.mutateAsync(l.id);
                navigate('/app/dashboard');
              }}
            >
              Delete
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            View only — link created by someone else.
          </p>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_280px]">
        <section className="card p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Performance</h2>
            <div className="flex gap-1 text-xs">
              {(['7d', '30d', '90d', 'all'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`rounded px-2 py-1 ${
                    range === r ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <Stat label="Total clicks" value={compactNumber(stats.data?.totals.total_clicks ?? 0)} />
            <Stat label="Unique visitors" value={compactNumber(stats.data?.totals.unique_visitors ?? 0)} />
          </div>

          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.data?.timeseries ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="clicks" stroke="#2563eb" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="uniques" stroke="#10b981" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="card p-5">
          <h2 className="text-sm font-semibold text-slate-700">QR code</h2>
          <p className="mt-1 text-xs text-slate-500">
            Encodes {shortUrl}
          </p>
          <div className="mt-3">
            <QRPreview value={shortUrl} />
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <BreakdownCard title="Countries" rows={stats.data?.byCountry ?? []} />
        <BreakdownCard title="Referrers" rows={stats.data?.byReferrer ?? []} />
        <BreakdownCard title="Devices" rows={stats.data?.byDevice ?? []} />
        <BreakdownCard title="Browsers" rows={stats.data?.byBrowser ?? []} />
      </div>

      <section className="card mt-6 p-5">
        <h2 className="text-sm font-semibold text-slate-700">Details</h2>
        <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm text-slate-700">
          <dt className="text-slate-500">Active</dt><dd>{l.is_active ? 'Yes' : 'No'}</dd>
          <dt className="text-slate-500">Created</dt><dd>{formatDate(l.created_at)}</dd>
          <dt className="text-slate-500">Updated</dt><dd>{formatDate(l.updated_at)}</dd>
          <dt className="text-slate-500">Expires</dt><dd>{formatDate(l.expires_at)}</dd>
          <dt className="text-slate-500">Click cap</dt>
          <dd>{l.max_clicks != null ? `${l.click_count} / ${l.max_clicks}` : 'None'}</dd>
        </dl>
      </section>

      <Modal open={editing} onClose={() => setEditing(false)} title="Edit link">
        <LinkForm
          submitLabel="Save"
          busy={update.isPending}
          initial={{
            destination_url: l.destination_url,
            title: l.title ?? undefined,
            expires_at: l.expires_at ? new Date(l.expires_at).toISOString() : undefined,
            max_clicks: l.max_clicks ?? undefined,
          }}
          onCancel={() => setEditing(false)}
          onSubmit={async (values) => {
            await update.mutateAsync({
              destination_url: values.destination_url,
              title: values.title,
              expires_at: values.expires_at ?? null,
              max_clicks: values.max_clicks ?? null,
            });
            setEditing(false);
          }}
        />
      </Modal>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function BreakdownCard({ title, rows }: { title: string; rows: StatsBreakdown[] }) {
  return (
    <section className="card p-5">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No data in this range.</p>
      ) : (
        <div className="mt-3 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows.slice(0, 10)} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="key"
                tick={{ fontSize: 11, fill: '#64748b' }}
                width={90}
              />
              <Tooltip />
              <Bar dataKey="clicks" fill="#2563eb" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
